"use client";
import { create } from "zustand";

export type Panel = "chat" | "section" | "project" | "marketplace" | "settings" | "tasks" | null;

interface UIState {
  activePanel: Panel;
  setActivePanel: (panel: Panel) => void;
  togglePanel: (panel: Panel) => void;

  showMiniMap: boolean;
  toggleMiniMap: () => void;

  chatMessages: Record<string, { role: "user" | "assistant"; content: string; surggieId?: string; toolCalls?: { name: string; params?: Record<string, unknown>; result?: unknown }[] }[]>;
  activeSurggieId: string | null;
  setActiveSurggieId: (id: string | null) => void;
  addChatMessage: (msg: UIState["chatMessages"][string][number], surggieId?: string) => void;
  clearChat: (surggieId?: string) => void;

  notifications: { id: string; message: string; type: "info" | "success" | "xp" }[];
  addNotification: (n: Omit<UIState["notifications"][number], "id">) => void;
  removeNotification: (id: string) => void;
}

let notifId = 0;

function createUIStore() {
  return create<UIState>((set) => ({
    activePanel: null,
    setActivePanel: (panel) => set({ activePanel: panel }),
    togglePanel: (panel) =>
      set((s) => ({ activePanel: s.activePanel === panel ? null : panel })),

    showMiniMap: true,
    toggleMiniMap: () => set((s) => ({ showMiniMap: !s.showMiniMap })),

    chatMessages: {},
    activeSurggieId: null,
    setActiveSurggieId: (id) => set({ activeSurggieId: id }),
    addChatMessage: (msg, surggieId) =>
      set((s) => {
        const key = surggieId ?? "default";
        return {
          chatMessages: {
            ...s.chatMessages,
            [key]: [...(s.chatMessages[key] ?? []), msg],
          },
        };
      }),
    clearChat: (surggieId) =>
      set((s) => {
        if (!surggieId) return { chatMessages: {} };
        const next = { ...s.chatMessages };
        delete next[surggieId];
        return { chatMessages: next };
      }),

    notifications: [],
    addNotification: (n) =>
      set((s) => ({
        notifications: [...s.notifications, { ...n, id: `notif_${++notifId}` }],
      })),
    removeNotification: (id) =>
      set((s) => ({
        notifications: s.notifications.filter((n) => n.id !== id),
      })),
  }));
}

/// Singleton: survive HMR module reloads so in-flight notifications aren't lost.
const _g = globalThis as Record<string, unknown>;
if (!_g.__surggiesUIStore) _g.__surggiesUIStore = createUIStore();
export const useUIStore = _g.__surggiesUIStore as ReturnType<typeof createUIStore>;
